import { useEffect, useMemo, useRef, useState } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { getReportId, getTemplateKey } from 'reporting-data/report/reportGetters';
import { EDITOR_TYPES, isQuickFilterEditorSchema } from 'reporting-data/tsTypes/editorTypes';
import { getDynamicReportEditors } from '../getDynamicReportEditors';
import { editorMatchesCategory, getEditorSchemasFromConfig } from './editorUtils';
import { EDITORS } from './editors';
import { useSingleObjectReportProperties } from './useSingleObjectReportProperties';
import { jsx as _jsx } from "react/jsx-runtime";
export const editorSchemasEqual = (schemasA, schemasB) => schemasA.length === schemasB.length && schemasA.every((schemaA, i) => {
  const schemaB = schemasB[i];
  if (schemaA.type !== schemaB.type) {
    return false;
  }
  if (isQuickFilterEditorSchema(schemaA)) {
    const filterSchemaB = schemaB;
    return schemaA.property === filterSchemaB.property && schemaA.operator === filterSchemaB.operator;
  }
  return true;
});
const editorsEqual = (editorsA, editorsB) => editorsA.length === editorsB.length && editorsA.every((editor, i) => editor === editorsB[i]);
export const useEditorComponents = (report, editorSchemas, overrideEditors, category) => {
  const prevReport = useRef(report);
  const [editorComponents, setEditorComponents] = useState([]);
  const editorMatches = editorSchemas.map(schema => (overrideEditors || EDITORS).find(editor => editor.matchesEditorType(report, schema) && editorMatchesCategory(editor, category)));
  const prevEditorMatches = usePrevious(editorMatches);
  const prevEditorSchemas = usePrevious(editorSchemas);
  useEffect(() => {
    const isReportChanged = getTemplateKey(report) !== getTemplateKey(prevReport.current) || getReportId(report) !== getReportId(prevReport.current);
    if (isReportChanged) {
      prevReport.current = report;
    }
    if (isReportChanged || !prevEditorSchemas || !prevEditorMatches || !editorsEqual(editorMatches, prevEditorMatches) || !editorSchemasEqual(editorSchemas, prevEditorSchemas)) {
      const newEditorComponents = editorMatches.reduce((memo, editorMatch, i) => {
        if (!editorMatch) {
          return memo;
        }
        const {
          EditorComponent
        } = editorMatch;
        const schema = editorSchemas[i];
        const EditorWithSchema = props => {
          return /*#__PURE__*/_jsx(EditorComponent, Object.assign({
            schema: schema
          }, props));
        };
        const schemaStr = JSON.stringify(schema);
        EditorWithSchema.displayName = `EditorWithSchema(${schemaStr}})`;
        memo.push(EditorWithSchema);
        return memo;
      }, []);
      setEditorComponents(newEditorComponents);
    }
  }, [report, editorMatches, prevEditorMatches, editorSchemas, prevEditorSchemas]);
  return editorComponents;
};
export const useEditorsFromConfig = report => {
  const editorSchemas = getEditorSchemasFromConfig(report);
  return useEditorComponents(report, editorSchemas);
};
export const useDynamicallyGeneratedEditorSchemas = report => {
  const {
    properties
  } = useSingleObjectReportProperties(report);
  return useMemo(() => getDynamicReportEditors(report, properties), [properties, report]);
};
export const useDynamicallyGeneratedReportEditors = report => {
  const dynamicEditors = useDynamicallyGeneratedEditorSchemas(report);
  const dynamicEditorComponents = useEditorComponents(report, dynamicEditors);
  return dynamicEditorComponents;
};
export const useQuickFilterSchemas = report => {
  const [quickFilterSchemas, setQuickFilterSchemas] = useState([]);
  const dynamicFilters = useDynamicallyGeneratedEditorSchemas(report);
  useEffect(() => {
    const configEditors = getEditorSchemasFromConfig(report);
    const newQuickFilterSchemas = configEditors.concat(dynamicFilters).filter(schema => schema.type === EDITOR_TYPES.QUICK_FILTER);
    if (!editorSchemasEqual(quickFilterSchemas, newQuickFilterSchemas)) {
      setQuickFilterSchemas(newQuickFilterSchemas);
    }
  }, [dynamicFilters, quickFilterSchemas, report]);
  return quickFilterSchemas;
};